import { useEffect, useState, useCallback } from "react";
import { api, apiError, fmtINR } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { StatusBadge } from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { Warning, ShieldCheck, EnvelopeSimple, Plus, PlusCircle, CalendarBlank, Images, Phone, WhatsappLogo, LockSimple, MapPin, CheckCircle } from "@phosphor-icons/react";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { JobMediaUploads } from "../../components/JobMediaUploads";

const TABS = [
  { key: "", label: "All" },
  { key: "assigned", label: "To Accept" },
  { key: "accepted", label: "Accepted" },
  { key: "in_progress", label: "In Progress" },
  { key: "completed", label: "Completed" },
];

export default function PartnerJobs() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("");
  const [otpJob, setOtpJob] = useState(null); // { job, kind }
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [engineers, setEngineers] = useState([]);
  const [addonJob, setAddonJob] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [addCat, setAddCat] = useState("");
  const [qtys, setQtys] = useState({}); // itemId -> qty
  const [custom, setCustom] = useState({ name: "", rate: "", qty: 1 });
  const [addBusy, setAddBusy] = useState(false);
  const [rescheduleJob, setRescheduleJob] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({ scheduled_date: "", slot: "", remarks: "" });
  const [rescheduleBusy, setRescheduleBusy] = useState(false);
  const [mediaJob, setMediaJob] = useState(null);
  const [revealed, setRevealed] = useState({}); // jobId -> { customer_phone, whatsapp_url }

  const reveal = useCallback(async (jid) => {
    if (revealed[jid]) return revealed[jid];
    const { data } = await api.post(`/partner/jobs/${jid}/reveal-contact`);
    setRevealed((m) => ({ ...m, [jid]: data }));
    return data;
  }, [revealed]);

  const doCall = async (j) => {
    try {
      const info = await reveal(j.id);
      if (info?.customer_phone) window.location.href = `tel:${info.customer_phone}`;
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Contact not available yet"); }
  };
  const doWhatsapp = async (j) => {
    try {
      const info = await reveal(j.id);
      if (info?.whatsapp_url) window.open(info.whatsapp_url, "_blank", "noopener");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Contact not available yet"); }
  };

  const openReschedule = (j) => { setRescheduleJob(j); setRescheduleForm({ scheduled_date: j.scheduled_date || "", slot: j.slot || "", remarks: "" }); };
  const doReschedule = async () => {
    if (!rescheduleForm.scheduled_date) { toast.error("Select a new date"); return; }
    setRescheduleBusy(true);
    try {
      await api.post(`/partner/jobs/${rescheduleJob.id}/reschedule`, rescheduleForm);
      toast.success("Appointment rescheduled");
      setRescheduleJob(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setRescheduleBusy(false); }
  };

  const load = useCallback(() => {
    api.get("/partner/jobs", { params: { status: tab } }).then((r) => setRows(r.data));
  }, [tab]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/partner/engineers").then((r) => setEngineers(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/partner/estimate-catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, []);

  const openAddons = (j) => {
    setAddonJob(j); setQtys({}); setCustom({ name: "", rate: "", qty: 1 });
    setAddCat(catalog[0]?.product_category || "");
  };

  const submitAddons = async () => {
    const lines = Object.entries(qtys).filter(([, q]) => q > 0).map(([item_id, qty]) => ({ item_id, qty }));
    const customArr = custom.name && Number(custom.rate) > 0 ? [{ name: custom.name, rate: Number(custom.rate), qty: Number(custom.qty) || 1 }] : [];
    if (lines.length === 0 && customArr.length === 0) { toast.error("Add at least one item"); return; }
    setAddBusy(true);
    try {
      const { data } = await api.post(`/partner/jobs/${addonJob.id}/addons`, { lines, custom: customArr });
      toast.success(`${data.added} item(s) sent to customer for approval`);
      setAddonJob(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAddBusy(false); }
  };

  const assignEngineer = async (jid, engineer_id) => {
    try {
      await api.post(`/partner/jobs/${jid}/assign-engineer`, { engineer_id });
      toast.success("Engineer assigned");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const act = async (jid, status) => {
    try {
      await api.post(`/partner/jobs/${jid}/status`, { status });
      toast.success(`Job ${status.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const markPaymentReceived = async (j) => {
    setBusy(true);
    try {
      await api.post(`/partner/jobs/${j.id}/payment-received`);
      toast.success("Additional charges payment marked as received");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const completeDirect = async (j) => {
    setBusy(true);
    try {
      await api.post(`/partner/jobs/${j.id}/complete-direct`);
      toast.success("Job completed");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openOtp = async (job, kind) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/partner/jobs/${job.id}/request-otp?kind=${kind}`);
      setOtpJob({ job, kind }); setOtp("");
      toast.success(`Code sent to customer's email (${data.email_hint || ""})`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) { toast.error("Enter the 6-digit code from the customer"); return; }
    setBusy(true);
    try {
      await api.post(`/partner/jobs/${otpJob.job.id}/verify-otp`, { kind: otpJob.kind, otp });
      toast.success(otpJob.kind === "start" ? "Job started" : "Job completed");
      setOtpJob(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const actions = (j) => {
    const routed = j.additional_charges_payment_to === "partner";
    const paid = j.additional_charges_payment_status === "received";
    const payBtn = (routed && !paid && ["accepted", "in_progress"].includes(j.status)) ? (
      <Button size="sm" variant="outline" className="rounded-sm gap-1 text-green-700 border-green-200 hover:bg-green-50" onClick={() => markPaymentReceived(j)} disabled={busy} data-testid={`job-payment-received-${j.id}`}><CheckCircle size={13} weight="fill" /> Payment Received</Button>
    ) : null;
    const addonBtn = ["assigned", "accepted", "in_progress"].includes(j.status) ? (
      <>
        <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={() => openAddons(j)} data-testid={`job-addons-${j.id}`}><Plus size={13} weight="bold" /> Items</Button>
        <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={() => setMediaJob(j)} data-testid={`job-media-${j.id}`}><Images size={13} weight="bold" /> Media</Button>
      </>
    ) : null;
    if (j.status === "assigned") return (
      <div className="flex justify-end gap-1 flex-wrap">
        <Button size="sm" className="rounded-sm" onClick={() => act(j.id, "accepted")} data-testid={`job-accept-${j.id}`}>Accept</Button>
        <Button size="sm" variant="outline" className="rounded-sm text-destructive" onClick={() => act(j.id, "rejected")} data-testid={`job-reject-${j.id}`}>Reject</Button>
        {addonBtn}
      </div>
    );
    if (j.status === "accepted") return (
      <div className="flex justify-end gap-1 flex-wrap">
        <Button size="sm" className="rounded-sm gap-1.5" onClick={() => openOtp(j, "start")} disabled={busy} data-testid={`job-start-${j.id}`}><ShieldCheck size={14} /> Start (OTP)</Button>
        {payBtn}{addonBtn}
      </div>
    );
    if (j.status === "in_progress") return (
      <div className="flex justify-end gap-1 flex-wrap">
        {routed ? (
          <Button size="sm" className="rounded-sm gap-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50" onClick={() => completeDirect(j)} disabled={busy || !paid} title={paid ? "" : "Mark Payment Received first"} data-testid={`job-completed-${j.id}`}><CheckCircle size={14} weight="fill" /> Job Completed</Button>
        ) : (
          <Button size="sm" className="rounded-sm gap-1.5 bg-green-600 hover:bg-green-700" onClick={() => openOtp(j, "end")} disabled={busy} data-testid={`job-complete-${j.id}`}><ShieldCheck size={14} /> Complete (OTP)</Button>
        )}
        {payBtn}{addonBtn}
      </div>
    );
    return <span className="text-xs text-muted-foreground">No action</span>;
  };

  const addonSummary = (j) => {
    const a = j.addons || [];
    if (!a.length) return null;
    const pend = a.filter((x) => x.status === "pending").length;
    const acc = a.filter((x) => x.status === "accepted").length;
    return <div className="text-[11px] text-muted-foreground mt-0.5">Add-ons: {acc} approved{pend ? `, ${pend} awaiting customer` : ""}</div>;
  };

  return (
    <>
      <PageHeader title="My Jobs" subtitle="Accept and update your assigned jobs" />
      <div className="p-8 space-y-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-sm" data-testid="job-tabs">
            {TABS.map((t) => <TabsTrigger key={t.key} value={t.key} className="rounded-sm" data-testid={`jtab-${t.label.toLowerCase().replace(/ /g, "-")}`}>{t.label}</TabsTrigger>)}
          </TabsList>
        </Tabs>
        <div className="border border-border rounded-sm bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead>Service</TableHead><TableHead>Customer</TableHead>
                <TableHead>Schedule</TableHead><TableHead>Address</TableHead>
                <TableHead>Engineer</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="partner-jobs-table">
              {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No jobs</TableCell></TableRow>}
              {rows.map((j) => (
                <TableRow key={j.id} className={j.status === "assigned" ? "bg-amber-50/60" : "hover:bg-secondary/30"}>
                  <TableCell>
                    <div className="font-semibold flex items-center gap-1.5">
                      {j.status === "assigned" && <Warning size={14} weight="fill" className="text-amber-500" />}
                      {j.service_type}
                    </div>
                    <div className="text-xs text-muted-foreground">{j.product_category} {j.product_type && `· ${j.product_type}`}{j.part_name && ` · ${j.part_name}`}</div>
                    {addonSummary(j)}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{j.customer_name || "—"}</div>
                    {(() => {
                      const info = revealed[j.id];
                      const display = info?.customer_phone || j.customer_phone_masked || "—";
                      const unlocked = j.contact_unlocked;
                      if (display === "—") return <div className="text-xs text-muted-foreground mt-0.5">No contact</div>;
                      return (
                        <div className="mt-1">
                          <div className={`text-xs font-mono ${unlocked ? "text-foreground" : "text-muted-foreground select-none pointer-events-none"}`} data-testid={`job-phone-${j.id}`}>
                            {display}
                          </div>
                          <div className="flex items-center gap-1 mt-1">
                            {unlocked ? (
                              <>
                                <Button size="sm" variant="outline" className="h-7 rounded-sm gap-1 px-2 text-xs" onClick={() => doCall(j)} data-testid={`job-call-${j.id}`}>
                                  <Phone size={12} weight="fill" /> Call
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 rounded-sm gap-1 px-2 text-xs text-green-700 border-green-200 hover:bg-green-50" onClick={() => doWhatsapp(j)} data-testid={`job-whatsapp-${j.id}`}>
                                  <WhatsappLogo size={13} weight="fill" /> WhatsApp
                                </Button>
                              </>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" data-testid={`job-contact-locked-${j.id}`}>
                                <LockSimple size={11} weight="fill" /> Unlocks after scheduling
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-sm">{(() => {
                    const d = j.scheduled_date || j.preferred_service_date;
                    const s = j.scheduled_date ? j.slot : j.preferred_slot;
                    const isPreferred = !j.scheduled_date && j.preferred_service_date;
                    if (!d) return "—";
                    return (<>
                      {d}{s ? <div className="text-xs text-muted-foreground">{s}</div> : null}
                      {isPreferred && <div className="text-[10px] text-indigo-600 font-semibold">customer-preferred</div>}
                    </>);
                  })()}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[260px] whitespace-normal align-top" data-testid={`job-address-${j.id}`}>
                    {[j.address, j.city, j.state].filter(Boolean).join(", ") || "—"}{j.pincode ? ` - ${j.pincode}` : ""}
                    {j.maps_location && (
                      <a href={j.maps_location} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid={`job-location-${j.id}`}>
                        <MapPin size={12} weight="fill" /> Open Location
                      </a>
                    )}
                    {j.customer_remarks && <div className="mt-1 text-[11px] italic text-muted-foreground">📝 {j.customer_remarks}</div>}
                  </TableCell>
                  <TableCell>
                    {["completed", "cancelled", "rejected"].includes(j.status) ? (
                      <span className="text-xs text-muted-foreground">{j.engineer_name || "—"}</span>
                    ) : (
                      <Select value={j.engineer_id || ""} onValueChange={(v) => assignEngineer(j.id, v)}>
                        <SelectTrigger className="rounded-sm h-8 text-xs w-[150px]" data-testid={`assign-engineer-${j.id}`}>
                          <SelectValue placeholder="Assign engineer" />
                        </SelectTrigger>
                        <SelectContent>
                          {engineers.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">No engineers added</div>}
                          {engineers.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.pincode}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono">{fmtINR(j.amount)}</TableCell>
                  <TableCell><StatusBadge value={j.status === "in_progress" ? "pending" : (j.status === "completed" ? "complete" : j.status)} /></TableCell>
                  <TableCell className="text-right">{actions(j)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!mediaJob} onOpenChange={(o) => !o && setMediaJob(null)}>
        <DialogContent className="max-w-lg" data-testid="partner-media-dialog">
          <DialogHeader><DialogTitle className="font-heading">Job Media Upload</DialogTitle>
          <DialogDescription>Capture the required photos/videos/documents for this job. Files are auto-compressed (max 5MB). Required items must be uploaded before completing the job.</DialogDescription></DialogHeader>
          {mediaJob && (
            <div className="py-2 max-h-[60vh] overflow-y-auto">
              <div className="bg-secondary rounded-sm p-3 text-sm mb-3">
                <p className="font-semibold">{mediaJob.service_type} · {mediaJob.product_category}</p>
                <p className="text-xs text-muted-foreground">{mediaJob.brand_name ? `Brand: ${mediaJob.brand_name} · ` : ""}{mediaJob.customer_name || "—"}</p>
              </div>
              <JobMediaUploads jobId={mediaJob.id} canUpload />
            </div>
          )}
          <DialogFooter>
            <Button className="rounded-sm" onClick={() => setMediaJob(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!otpJob} onOpenChange={(o) => !o && setOtpJob(null)}>
        <DialogContent data-testid="job-otp-dialog">
          <DialogHeader><DialogTitle className="font-heading">{otpJob?.kind === "start" ? "Start Job — Customer OTP" : "Complete Job — Customer OTP"}</DialogTitle>
          <DialogDescription>Enter the 6-digit code the customer received by email to {otpJob?.kind === "start" ? "start" : "complete"} this job.</DialogDescription></DialogHeader>
          {otpJob && (
            <div className="space-y-4 py-2 text-center">
              <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center mx-auto"><EnvelopeSimple size={26} weight="bold" className="text-primary" /></div>
              <p className="text-sm text-muted-foreground">We emailed a 6-digit code to <strong>{otpJob.job.customer_name}</strong>. Ask the customer for the code and enter it to {otpJob.kind === "start" ? "start" : "complete"} the job.</p>
              <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6} placeholder="000000"
                className="rounded-sm text-center text-2xl tracking-[0.4em] font-bold h-14 max-w-[200px] mx-auto" data-testid="job-otp-input" />
              <button className="text-sm text-primary font-semibold hover:underline" onClick={() => openOtp(otpJob.job, otpJob.kind)} data-testid="job-otp-resend">Resend code</button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOtpJob(null)}>Cancel</Button>
            <Button className="rounded-sm" onClick={verifyOtp} disabled={busy} data-testid="job-otp-verify">{busy ? "Verifying…" : "Verify & Proceed"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!addonJob} onOpenChange={(o) => !o && setAddonJob(null)}>
        <DialogContent className="max-w-lg" data-testid="addon-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add additional items</DialogTitle>
          <DialogDescription>Pick items the job needs. These are sent to the customer for approval before you perform them.</DialogDescription></DialogHeader>
          {addonJob && (
            <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
              {(addonJob.addons || []).length > 0 && (
                <div className="bg-secondary rounded-sm p-3 text-xs space-y-1" data-testid="addon-existing">
                  <p className="font-semibold">Existing add-ons</p>
                  {addonJob.addons.map((a) => (
                    <div key={a.id} className="flex justify-between"><span>{a.name} × {a.qty}</span>
                      <span className={a.status === "accepted" ? "text-green-600" : a.status === "rejected" ? "text-destructive" : "text-amber-600"}>{fmtINR(a.total)} · {a.status}</span></div>
                  ))}
                </div>
              )}
              <div>
                <Label className="label-caps">Category</Label>
                <Select value={addCat} onValueChange={setAddCat}>
                  <SelectTrigger className="rounded-sm mt-1.5" data-testid="addon-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{catalog.map((c) => <SelectItem key={c.product_category} value={c.product_category}>{c.product_category}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                {(catalog.find((c) => c.product_category === addCat)?.items || []).map((it) => (
                  <div key={it.id} className="flex items-center justify-between gap-3 border border-border rounded-sm px-3 py-2" data-testid={`addon-item-${it.id}`}>
                    <div className="min-w-0"><p className="text-sm font-medium truncate">{it.name}</p><p className="text-xs text-primary">{fmtINR(it.rate)} · {it.unit}</p></div>
                    <Input type="number" min={0} value={qtys[it.id] || ""} placeholder="0" onChange={(e) => setQtys((q) => ({ ...q, [it.id]: Math.max(0, Number(e.target.value) || 0) }))} className="rounded-sm h-8 w-20 text-center" data-testid={`addon-qty-${it.id}`} />
                  </div>
                ))}
                {catalog.length === 0 && <p className="text-xs text-muted-foreground">No item catalog configured yet. Use the custom item below.</p>}
              </div>
              <div className="border-t border-border pt-3">
                <Label className="label-caps">Or add a custom item</Label>
                <div className="grid grid-cols-6 gap-2 mt-1.5">
                  <Input placeholder="Item name" value={custom.name} onChange={(e) => setCustom({ ...custom, name: e.target.value })} className="rounded-sm col-span-3" data-testid="addon-custom-name" />
                  <Input type="number" placeholder="Rate" value={custom.rate} onChange={(e) => setCustom({ ...custom, rate: e.target.value })} className="rounded-sm col-span-2" data-testid="addon-custom-rate" />
                  <Input type="number" placeholder="Qty" value={custom.qty} onChange={(e) => setCustom({ ...custom, qty: e.target.value })} className="rounded-sm" data-testid="addon-custom-qty" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setAddonJob(null)}>Cancel</Button>
            <Button className="rounded-sm gap-1.5" onClick={submitAddons} disabled={addBusy} data-testid="addon-submit"><PlusCircle size={15} weight="bold" /> {addBusy ? "Sending…" : "Send for approval"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!rescheduleJob} onOpenChange={(o) => !o && setRescheduleJob(null)}>
        <DialogContent data-testid="partner-reschedule-dialog">
          <DialogHeader><DialogTitle className="font-heading">Reschedule appointment</DialogTitle>
          <DialogDescription>Update the date/time when the customer requests a reschedule.</DialogDescription></DialogHeader>
          {rescheduleJob && (
            <div className="space-y-4 py-2">
              <div className="bg-secondary rounded-sm p-3 text-sm">
                <p className="font-semibold">{rescheduleJob.service_type}</p>
                <p className="text-xs text-muted-foreground">Currently {rescheduleJob.scheduled_date || "—"} {rescheduleJob.slot}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="label-caps">New Date</Label>
                  <Input type="date" value={rescheduleForm.scheduled_date} onChange={(e) => setRescheduleForm({ ...rescheduleForm, scheduled_date: e.target.value })} className="rounded-sm mt-1.5" data-testid="partner-reschedule-date" /></div>
                <div><Label className="label-caps">Time Slot</Label>
                  <Input value={rescheduleForm.slot} onChange={(e) => setRescheduleForm({ ...rescheduleForm, slot: e.target.value })} placeholder="e.g. 2:00 PM - 4:00 PM" className="rounded-sm mt-1.5" data-testid="partner-reschedule-slot" /></div>
              </div>
              <div><Label className="label-caps">Remarks</Label>
                <Input value={rescheduleForm.remarks} onChange={(e) => setRescheduleForm({ ...rescheduleForm, remarks: e.target.value })} placeholder="Reason / note" className="rounded-sm mt-1.5" data-testid="partner-reschedule-remarks" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setRescheduleJob(null)}>Cancel</Button>
            <Button className="rounded-sm" onClick={doReschedule} disabled={rescheduleBusy} data-testid="partner-reschedule-confirm">{rescheduleBusy ? "Saving…" : "Reschedule"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
